import { useEffect, useState } from 'react'
import {
  usePreloadedAnswers,
  useReadOnlyMode,
} from '../../BriefingFormDataProvider'
import { BriefColorPicker } from './BriefColorPicker'
import { BriefAddColorBtn } from './BriefAddColorBtn'
import { generateRandomStr } from '@utils/utilities'
import { IUnifiedPickerConfig } from '@pageFeatures/design/briefingForm/briefingFormProductColors.config'
import { IUnifiedPickerTabConfig } from '@pageFeatures/design/unifiedColorPicker/unifiedColorPicker.types'
import { optionKeyToKebabCase } from '@pageFeatures/design/briefingForm/utils'

const generateLegacySelectedColors = (preloadedAnswers) => {
  const legacySelectedColors = []
  for (const [key, value] of Object.entries(preloadedAnswers)) {
    if (key.startsWith('color-')) {
      legacySelectedColors.push({ key: value })
    }
  }

  legacySelectedColors.sort((a, b) => {
    const [keya] = Object.entries(a)[0]
    const [keyb] = Object.entries(b)[0]
    const numa = Number(keya.split('-')[1])
    const numb = Number(keyb.split('-')[1])
    return numa <= numb ? -1 : 1
  })
  return legacySelectedColors
}

const generatePickersBySelectedColors = (
  selectedColors: Record<string, string>[],
  config: IUnifiedPickerTabConfig[]
) => {
  const filledPickers: IUnifiedPickerConfig[] = selectedColors.map(
    (selectedColor, index) => {
      const [key, color] = Object.entries(selectedColor)[0]
      return {
        key,
        defaultValue: color as string,
        pickerLabel: `Color #${index + 1}`,
        pickerConfig: config,
      }
    }
  )
  return filledPickers
}

interface IBriefColorSelection {
  initialPickersConfig: IUnifiedPickerConfig[]
  mode: 'manual' | string
  cap: number
  pickerTemplate: IUnifiedPickerTabConfig[]
  onChange: (selectedColors) => void
}

export function BriefColorSelection({
  initialPickersConfig,
  mode = null,
  cap = 10,
  pickerTemplate,
  onChange,
}: IBriefColorSelection) {
  const [pickers, setPickers] = useState<IUnifiedPickerConfig[]>([])
  const preloadedAnswers = usePreloadedAnswers()
  const readOnlyMode = useReadOnlyMode()

  const onColorSelection = (key: string, color: string) => {
    const changedPickers = pickers.map((picker) => {
      if (picker.key === key) {
        picker.defaultValue = color
      }
      return picker
    })
    setPickers(changedPickers)
  }

  const addDynamicColorPicker = (color) => {
    if (pickers.length >= cap) {
      return
    }

    if (!color) {
      return
    }

    const nextPickerLabelIndex = pickers.length + 1
    const newPicker: IUnifiedPickerConfig = {
      pickerLabel: `Color #${nextPickerLabelIndex}`,
      pickerConfig: pickerTemplate,
      key: generateRandomStr(),
      defaultValue: color,
    }
    setPickers([...pickers, newPicker])
  }

  const removeColorPicker = (key: string) => {
    const filteredPickers = pickers.filter((picker) => picker.key !== key)

    const updatedPickers = filteredPickers.map((picker, index) => {
      return {
        ...picker,
        defaultValue: picker.defaultValue,
        pickerLabel: `Color #${index + 1}`,
        key: picker.key,
      }
    })

    setPickers(updatedPickers)
  }

  useEffect(() => {
    if (preloadedAnswers) {
      let selectedColors = preloadedAnswers['selected-colors'] || []

      if (mode === 'manual') {
        if (selectedColors.length < 1) {
          selectedColors = generateLegacySelectedColors(preloadedAnswers)
        }

        const filledPickers = generatePickersBySelectedColors(
          selectedColors,
          pickerTemplate
        )
        setPickers(filledPickers)
      } else {
        const filledPickers = initialPickersConfig.map((config) => {
          const selectedColor = selectedColors.find((item) => item[config.key])
          config.defaultValue = selectedColor?.[config.key]
          return config
        })

        setPickers(filledPickers)
      }
    } else {
      setPickers(initialPickersConfig)
    }
  }, [readOnlyMode, mode, preloadedAnswers, initialPickersConfig])

  useEffect(() => {
    onChange({
      'selected-colors': pickers.map((picker) => ({
        [optionKeyToKebabCase(picker.key)]: picker.defaultValue || '',
      })),
    })
  }, [pickers])

  return (
    <div className="flex flex-col justify-between w-full xs:flex-row xs:flex-wrap gap-y-4">
      {pickers?.map((option, index) => {
        return (
          <BriefColorPicker
            key={option.key}
            label={option.pickerLabel}
            initialValue={option.defaultValue}
            config={option.pickerConfig}
            readOnly={readOnlyMode}
            className="w-full xs:w-[calc(50%-5px)]"
            canBeRemoved={mode === 'manual'}
            onRemove={() => removeColorPicker(option.key)}
            onChange={(color) => onColorSelection(option.key, color)}
          />
        )
      })}

      {mode === 'manual' && pickers?.length < cap && !readOnlyMode && (
        <BriefAddColorBtn
          onSelection={(color) => addDynamicColorPicker(color)}
          pickerTemplate={pickerTemplate}
        />
      )}
    </div>
  )
}
